import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import type { FieldBaseProps } from './types';

export interface FieldNumberStepperProps<T extends FieldValues> extends FieldBaseProps<T> {
  min?: number;
  max?: number;
  step?: number;
}

/**
 * RHF-bound integer stepper (the prototype kit's `FieldPriorityStepper`): a
 * number input flanked by −/+ buttons, clamped to `[min, max]`. Stores a
 * numeric value. Used for the assignment-rule priority field.
 */
export function FieldNumberStepper<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  required,
  disabled,
  min = 1,
  max = 999,
  step = 1,
}: FieldNumberStepperProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const current = Number(field.value);
        const clamp = (n: number) => Math.min(Math.max(n, min), max);
        const bump = (delta: number) => {
          const base = Number.isFinite(current) ? current : min;
          field.onChange(clamp(base + delta));
        };
        return (
          <Box>
            {label && (
              <Typography
                component="label"
                sx={{ fontSize: 13, fontWeight: 600, display: 'block', mb: 0.5 }}
              >
                {label}
                {required && (
                  <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                    {' *'}
                  </Typography>
                )}
              </Typography>
            )}
            <TextField
              {...field}
              size="small"
              type="number"
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                field.onChange(raw === '' ? '' : Number(raw));
              }}
              required={required}
              disabled={disabled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? helperText}
              sx={{ maxWidth: 200 }}
              slotProps={{
                htmlInput: { min, max, step, inputMode: 'numeric', 'aria-label': label },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        aria-label="decrement"
                        disabled={disabled}
                        onClick={() => bump(-step)}
                        edge="start"
                      >
                        <RemoveOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="increment"
                        disabled={disabled}
                        onClick={() => bump(step)}
                        edge="end"
                      >
                        <AddOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        );
      }}
    />
  );
}
